import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { IssueDistribution } from "../../api";

interface Props {
  data: IssueDistribution[];
  loading?: boolean;
}

// Semantic colors: negative-heavy issues get warmer tones
function barColor(negPct: number): string {
  if (negPct >= 80) return "#e11d48";  // rose-600
  if (negPct >= 50) return "#f59e0b";  // amber-500
  if (negPct >= 20) return "#6b7280";  // gray-500
  return "#10b981";                     // emerald-500
}

export default function IssueDistChart({ data, loading }: Props) {
  if (loading) return <div className="h-52 rounded-xl bg-surface animate-pulse" />;
  if (!data.length) {
    return (
      <div className="flex h-52 items-center justify-center rounded-xl border border-dashed border-border bg-surface text-sm text-ink-tertiary">
        暂无问题分布数据
      </div>
    );
  }

  // Sort by total descending for better visual hierarchy
  const sorted = [...data].sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-3">
      {/* Bar chart */}
      <div className="rounded-xl border border-border bg-surface px-2 pt-3">
        <ResponsiveContainer width="100%" height={Math.max(200, sorted.length * 32 + 40)}>
          <BarChart
            data={sorted}
            layout="vertical"
            margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: "var(--chart-axis)" }} tickLine={false} axisLine={false} />
            <YAxis
              type="category"
              dataKey="issue_type"
              tick={{ fontSize: 11, fill: "var(--color-ink-secondary)" }}
              tickLine={false}
              axisLine={false}
              width={80}
            />
            <Tooltip
              contentStyle={{
                fontSize: 11,
                borderRadius: 10,
                border: "1px solid var(--chart-tooltip-border)",
                background: "var(--chart-tooltip-bg)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
              formatter={(value: number, name: string) => {
                const label = name === "negative" ? "负面" : name === "positive" ? "正面" : "中性";
                return [value, label];
              }}
              labelFormatter={(label: string) => label}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              iconType="circle"
              iconSize={7}
              formatter={(value: string) => (value === "negative" ? "负面" : value === "positive" ? "正面" : "中性")}
            />
            <Bar dataKey="negative" stackId="a" fill="#e11d48" radius={[0, 0, 0, 0]} />
            <Bar dataKey="neutral" stackId="a" fill="#a1a1aa" radius={[0, 0, 0, 0]} />
            <Bar dataKey="positive" stackId="a" fill="#10b981" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-1.5 px-1">
        {sorted.slice(0, 6).map((d) => (
          <span
            key={d.issue_type}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs"
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: barColor(d.neg_pct) }}
            />
            <span className="text-ink-secondary">{d.issue_type}</span>
            <span className="font-mono tabular-nums text-ink-faint">{d.total}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
